import styled from "styled-components";
import useAccountInfo from "features/teacher/hooks/account/useAccountInfo";
import Refresh from "shared/components/Refresh";
import { hw } from "features/homework/homeworkUi";

interface AccountInfoProps{
    clickedTeacher:string
}

const AccountInfo = ({clickedTeacher}:AccountInfoProps) => {
    const {teacher, onRefreshClick, studentCount, lessonName} = useAccountInfo(clickedTeacher);

    if (!clickedTeacher) {
        return (
            <Container>
                <Header>
                    <div>
                        <Title>교사정보</Title>
                        <Sub>오른쪽 목록에서 강사를 선택하면 상세 정보가 표시됩니다.</Sub>
                    </div>
                </Header>
                <Empty>
                    <EmptyTitle>강사를 선택하세요</EmptyTitle>
                    <EmptyText>목록에서 이름을 클릭하면 계정 정보를 확인할 수 있습니다.</EmptyText>
                </Empty>
            </Container>
        );
    }

    const rows = [
        { label: "성명", value: teacher.name },
        { label: "아이디", value: teacher.id },
        { label: "비밀번호", value: teacher.pw },
        { label: "전화번호", value: teacher.tel },
        { label: "이메일", value: teacher.email },
        { label: "담당학급", value: lessonName },
        { label: "원생수", value: studentCount === 0 ? "0" : String(studentCount) },
        { label: "주소", value: [teacher.address, teacher.address_detail].filter(Boolean).join(" ") },
    ];

    return (
        <Container>
            <Header>
                <div>
                    <Title>교사정보</Title>
                    <Sub>{teacher.name || "선택한 강사"}의 계정 정보입니다.</Sub>
                </div>
                <Refresh onRefreshClick={onRefreshClick}/>
            </Header>
            <Grid>
                {rows.map((row) => (
                    <Field key={row.label}>
                        <Label>{row.label}</Label>
                        <Value>{row.value || "-"}</Value>
                    </Field>
                ))}
            </Grid>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 20px 22px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 14px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`

const Title = styled.h2`
    margin: 0;
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
`

const Sub = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    overflow: auto;
    min-height: 0;
    align-content: start;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px;
    background: ${hw.bg};
    border: 1px solid ${hw.border};
    border-radius: 12px;
    min-width: 0;
`

const Label = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${hw.textMuted};
`

const Value = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${hw.text};
    word-break: break-all;
`

const Empty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    min-height: 240px;
`

const EmptyTitle = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: ${hw.text};
`

const EmptyText = styled.div`
    font-size: 13px;
    color: ${hw.textMuted};
    line-height: 1.5;
`

export default AccountInfo;
